import React, { useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Grid,
    makeStyles,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Chip from "@material-ui/core/Chip";
import Toolbar from "@material-ui/core/Toolbar";
import AppBar from "@material-ui/core/AppBar";
import { Paper } from "@mui/material";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    content: {
        padding: theme.spacing(2),
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: "300px", // Match the height from FloorManagement
    },
    formContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    stepContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    stepper: {
        padding: 0,
        backgroundColor: "transparent",
        marginBottom: theme.spacing(1),
        "& .MuiStepConnector-root": {
            left: "calc(-50% + 20px)",
            right: "calc(50% + 20px)",
        },
        "& .MuiStepIcon-root": {
            color: theme.palette.grey[300],
            "&.MuiStepIcon-active": {
                color: theme.palette.primary.main,
            },
            "&.MuiStepIcon-completed": {
                color: theme.palette.primary.main,
            },
        },
        "& .MuiStep-horizontal": {
            padding: 0,
        },
    },
    deviceList: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
        marginBottom: theme.spacing(1),
    },
    chipContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    buttonContainer: {
        marginTop: "auto",
        paddingTop: theme.spacing(2),
    },
    rightButtons: {
        marginLeft: "auto",
    },
}));

const DeviceAssignment = ({ devices, groups, onAssignDevices, isLoading }) => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const steps = ["Избор на устройства", "Избор на група"];

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleAssign = () => {
        onAssignDevices(selectedGroup, selectedDevices);
        setActiveStep(0);
        setSelectedDevices([]);
        setSelectedGroup(null);
    };

    const isStepValid = () => {
        if (activeStep === 0) {
            return selectedDevices.length > 0;
        }
        return selectedGroup !== null;
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <div className={classes.stepContent}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    value={selectedDevices}
                                    onChange={(event, newValue) => {
                                        setSelectedDevices(newValue);
                                    }}
                                    options={devices}
                                    getOptionLabel={(option) => option.name}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Избери устройства"
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    )}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <div style={{ flex: 1 }} /> {/* Spacer to push button down */}
                    </div>
                );
            case 1:
                return (
                    <div className={classes.stepContent}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Paper className={classes.deviceList}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Избрани устройства:
                                    </Typography>
                                    <div className={classes.chipContainer}>
                                        {selectedDevices.map((device) => (
                                            <Chip key={device.id} label={device.name} size="small" />
                                        ))}
                                    </div>
                                </Paper>
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    value={selectedGroup}
                                    onChange={(event, newValue) => {
                                        setSelectedGroup(newValue);
                                    }}
                                    options={groups}
                                    getOptionLabel={(option) => option.name}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Избери група"
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    )}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <div style={{ flex: 1 }} /> {/* Spacer to push button down */}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Card className={classes.root}>
            <AppBar position="static" elevation={0} color="primary">
                <Toolbar className="px-8">
                    <Typography
                        variant="subtitle1"
                        color="inherit"
                        className="flex-1 px-12"
                    >
                        Задаване на устройства към група
                    </Typography>
                </Toolbar>
            </AppBar>
            <CardContent className={classes.content}>
                <div className={classes.formContent}>
                    <Stepper activeStep={activeStep} className={classes.stepper}>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel className={classes.stepLabel}>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {renderStepContent(activeStep)}
                </div>

                <Grid container className={classes.buttonContainer}>
                    <Grid item xs={12}>
                        <div style={{ display: "flex", justifyContent: "flex-start" }}>
                            <Button
                                variant="outlined"
                                onClick={handleBack}
                                disabled={activeStep === 0}
                            >
                                Назад
                            </Button>
                            <div className={classes.rightButtons}>
                                {activeStep === steps.length - 1 ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleAssign}
                                        disabled={!isStepValid() || isLoading}
                                    >
                                        Задаване
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleNext}
                                        disabled={!isStepValid()}
                                    >
                                        Напред
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default DeviceAssignment;
